import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {TourService} from "../classes/tour.service";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {distinctUntilChanged, distinctUntilKeyChanged, filter, Observable, of, tap} from "rxjs";
import {Tour} from "../classes/tour.model";
import {MatButtonModule} from "@angular/material/button";
import {MatToolbarModule} from "@angular/material/toolbar";
import {AsyncPipe, CommonModule, NgIf} from "@angular/common";
import {LanguageService} from "../classes/language.service";
import {Analytics, logEvent} from "@angular/fire/analytics";

@Component({
  selector: 'app-tour',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatToolbarModule,
    RouterLink,
  ],
  templateUrl: './tour.component.html',
  styleUrl: './tour.component.scss'
})
export class TourComponent {
  @ViewChild('audioPlayerElement') audioPlayer: ElementRef<HTMLAudioElement>;
  tour$: Observable<Tour> = this.tourService.activeTour$;

  constructor(
    private tourService: TourService,
    private route: ActivatedRoute,
    private router: Router,
    private languageService: LanguageService,
    private analytics: Analytics,
  ) {
    this.route.params.subscribe(async params => {

      if(!params['id']) {
        console.error('No tour id provided');
        return;
      }

      const currentTour = this.tourService.activeTour$.value;
      if(currentTour?.id !== params['id']) {
        console.log('Id is different for the tours, reloading tour');
        const newTour = await this.tourService.loadTour(params['id'], true);
      }else{
        console.log('Id is the same for the tours, not reloading');
      }
    });

    /**
     * This is a hack to make sure the audio player is reloaded when the tour step changes.
     */
    this.tour$.pipe(
      filter((tour: Tour) => !!tour),
      distinctUntilKeyChanged('step')).subscribe((tour: Tour) => {
      if (this.audioPlayer) {
        this.audioPlayer.nativeElement.load();
      }

      /**
       * This is a small hack to make sure that the route is updated when the tour step changes.
       * Otherwise reloading the page would result in a different step being shown
       */
      this.router.navigate(['/tours/view', tour.id],);

      logEvent(this.analytics, 'view_item', {
        item_id: tour.id,
        item_name: tour.title,
        item_brand: 'AudioTour',
        item_category: 'Tour',
        item_variant: 'Tour',
        item_list_id: 'main_tour',
        item_list_name: 'Main Tour',
        index: tour.step,
        quantity: 1,
        price: 0,
        currency: 'EUR',
        "language": this.languageService.currentLang$.value
      });

      logEvent(this.analytics, 'view_tour_step', {
        "tour": "main_tour",
        "step_id": tour.id,
        "step": tour.step,
        "language": this.languageService.currentLang$.value
      });
    });
  }

  private logTourStepEvent(event: string, audioElement: HTMLAudioElement, extra: any = {}) {
    const played = this.stringifyAudioPlayedTime(audioElement);

    logEvent(this.analytics, event, {
      "tour": "main_tour",
      "type": "audio",
      "step": this.tourService.activeTour$.value.step,
      "step_id": this.tourService.activeTour$.value.id,
      "language": this.languageService.currentLang$.value,
      "audio_duration": audioElement.duration.toFixed(1),
      ...played,
      ...extra
    });
  }

  private stringifyAudioPlayedTime(audio: HTMLAudioElement) {
    const played = audio.played;
    const playedTime = [];
    let totalPlayedTime = 0;
    for (let i = 0; i < played.length; i++) {
      playedTime.push([played.start(i), played.end(i)]);
      totalPlayedTime += played.end(i) - played.start(i);
    }
    return {
      "played_parts": playedTime,
      "total_played_time": totalPlayedTime.toFixed(1)
    };
  }

  handleStartPlay($event: Event) {
    const audio = $event.target as HTMLAudioElement;

    this.logTourStepEvent('tour_step_play', audio);

  }

  handleAudioEnded($event: Event) {
    const audio = $event.target as HTMLAudioElement;

    this.logTourStepEvent('tour_step_ended', audio);
  }

  handlePause($event: Event) {
    const audio = $event.target as HTMLAudioElement;

    this.logTourStepEvent('tour_step_pause', audio, {
      "pause_time": audio.currentTime.toFixed(1),
    });
  }
}
